import React from "react";
import { motion } from "framer-motion";

export default function FiltersBar({
  facets = {},
  value,
  onChange,
  onClear,
  onClose, // opcionális (mobilon bezáráshoz)
  wizardSummaryChips = [],
  hubs = [], // [{id,name}]
}) {
  const v = value || {};
  const update = (patch) => onChange?.({ ...v, ...patch });

  const toggleInSet = (key, item) => {
    const set = new Set(v[key] || []);
    set.has(item) ? set.delete(item) : set.add(item);
    update({ [key]: Array.from(set) });
  };

  return (
    <motion.aside
      className="w-full md:w-64 bg-white border border-gray-200 rounded-2xl p-4 md:sticky md:top-4 md:h-fit"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold">Szűrők</h3>
        {onClose && (
          <button className="md:hidden text-sm text-gray-600" onClick={onClose}>
            Bezár
          </button>
        )}
      </div>

      {/* Előző lépésekből jövő aktív előszűrők */}
      {wizardSummaryChips.length > 0 && (
        <div className="mb-4">
          <div className="text-xs text-gray-600 mb-1">Aktív előszűrők</div>
          <div className="flex flex-wrap gap-2">
            {wizardSummaryChips.map((c, i) => (
              <span
                key={i}
                className="px-2 py-1 rounded-full text-xs bg-blue-50 border border-blue-200"
                title="Előlépésben beállítva – szerkeszthető"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* RENDEZÉS – FELÜLRE */}
      <div className="mb-4">
        <label className="text-xs text-gray-600">Rendezés</label>
        <select
          className="w-full border rounded px-3 py-2 mt-1"
          value={v.sort || "relevance"}
          onChange={(e) => update({ sort: e.target.value })}
        >
          <option value="relevance">Ajánlott</option>
          <option value="price-asc">Ár szerint (növ.)</option>
          <option value="price-desc">Ár szerint (csök.)</option>
          <option value="name-asc">Név szerint (A→Z)</option>
          <option value="name-desc">Név szerint (Z→A)</option>
        </select>
      </div>

      {/* ÁR */}
      <div className="mb-4">
        <label className="text-xs text-gray-600">Ár (Ft)</label>
        <div className="flex gap-2 mt-1">
          <input
            type="number"
            className="w-full border rounded px-2 py-1"
            placeholder={facets.price?.min ?? 0}
            value={v.priceMin ?? ""}
            onChange={(e) =>
              update({
                priceMin: e.target.value === "" ? undefined : Number(e.target.value),
              })
            }
          />
          <input
            type="number"
            className="w-full border rounded px-2 py-1"
            placeholder={facets.price?.max ?? 0}
            value={v.priceMax ?? ""}
            onChange={(e) =>
              update({
                priceMax: e.target.value === "" ? undefined : Number(e.target.value),
              })
            }
          />
        </div>
      </div>

      {/* HUB kompatibilitás – ÁR ALÁ */}
      <div className="mb-4">
        <label className="text-xs text-gray-600">Hub kompatibilitás</label>
        <select
          className="w-full border rounded px-3 py-2 mt-1"
          value={v.selectedHubId || ""}
          onChange={(e) => update({ selectedHubId: e.target.value || undefined })}
        >
          <option value="">— Válassz HUB‑ot —</option>
          {hubs.map((h) => (
            <option key={h.id} value={h.id}>
              {h.name}
            </option>
          ))}
        </select>
        {v.selectedHubId && (
          <p className="mt-2 text-xs text-gray-600">
            Csak a kiválasztott HUB‑bal működő termékeket listázzuk.
          </p>
        )}
      </div>

      {/* Keresés */}
      <div className="mb-4">
        <label className="text-xs text-gray-600">Keresés</label>
        <input
          className="w-full border rounded px-3 py-2"
          placeholder="Terméknév / leírás…"
          value={v.q || ""}
          onChange={(e) => update({ q: e.target.value })}
        />
      </div>

      {/* Elérhetőség */}
      <div className="mb-4">
        <label className="text-xs text-gray-600">Elérhetőség</label>
        <div className="mt-1 flex gap-2">
          <label className="text-sm flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!v.onlyInStock}
              onChange={(e) => update({ onlyInStock: e.target.checked })}
            />
            Raktáron
          </label>
        </div>
      </div>

      {/* Márka */}
      {Array.isArray(facets.brands) && facets.brands.length > 0 && (
        <div className="mb-4">
          <label className="text-xs text-gray-600">Márka</label>
          <div className="mt-1 flex flex-wrap gap-2">
            {facets.brands.map((b) => {
              const active = (v.brands || []).includes(b);
              return (
                <button
                  key={b}
                  type="button"
                  onClick={() => toggleInSet("brands", b)}
                  className={`px-3 py-1.5 rounded border text-sm ${
                    active ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-300"
                  }`}
                >
                  {b}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Protokollok */}
      {Array.isArray(facets.protocols) && facets.protocols.length > 0 && (
        <div className="mb-4">
          <label className="text-xs text-gray-600">Protokoll</label>
          <div className="mt-1 flex flex-wrap gap-2">
            {facets.protocols.map((p) => {
              const active = (v.protocols || []).includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => toggleInSet("protocols", p)}
                  className={`px-3 py-1.5 rounded border text-sm ${
                    active ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-300"
                  }`}
                >
                  {p}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Ökoszisztémák */}
      {facets.ecosystems && (
        <div className="mb-4">
          <label className="text-xs text-gray-600">Ökoszisztémák</label>
          <div className="mt-1 flex flex-col gap-1 text-sm">
            {["homekit", "google", "alexa", "matter"].map((eco) => (
              <label key={eco} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!v.ecosystems?.[eco]}
                  onChange={(e) =>
                    update({ ecosystems: { ...v.ecosystems, [eco]: e.target.checked } })
                  }
                />
                {eco}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* App‑vezérlés */}
      <div className="mb-4">
        <label className="text-xs text-gray-600">App‑vezérlés</label>
        <div className="mt-1 flex flex-col gap-1 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!v.directControl}
              onChange={(e) => update({ directControl: e.target.checked })}
              disabled={!!v.selectedHubId}
              title={v.selectedHubId ? "HUB kiválasztva — a lista csak HUB‑kompatibilist mutat" : ""}
            />
            Közvetlen app‑vezérlés elérhető
          </label>
        </div>
      </div>

      {/* Címkék */}
      {Array.isArray(facets.tags) && facets.tags.length > 0 && (
        <div className="mb-4">
          <label className="text-xs text-gray-600">Címkék</label>
          <div className="mt-1 flex flex-wrap gap-2">
            {facets.tags.map((t) => {
              const active = (v.tags || []).includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleInSet("tags", t)}
                  className={`px-2 py-1 rounded border text-xs ${
                    active ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-300"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          type="button"
          className="text-sm px-3 py-1.5 border rounded hover:bg-gray-50"
          onClick={onClear}
        >
          Szűrők törlése
        </button>
      </div>
    </motion.aside>
  );
}
